import { DT } from '@fafi/shared';

// Input-lead steering (§10.2). The client stamps inputs for a FUTURE server tick so they arrive just in
// time: stampTick = currentServerTick + ceil((rtt/2)/DT) + LEAD_BUFFER + LEAD_JITTER. Each snapshot
// exposes the arrival margin (ackedInputTick − tick); the client slews its lead ±1 to hold the margin in
// [MARGIN_MIN, MARGIN_MAX], and re-adapts (drops its slew offset) after an RTT change > RTT_READAPT_MS.

const DT_MS = DT * 1000;
export const LEAD_BUFFER = 2;
export const LEAD_JITTER = 1;
export const INITIAL_LEAD = 5; // before the first RTT sample
export const MARGIN_MIN = 1;
export const MARGIN_MAX = 3;
export const RTT_READAPT_MS = 30;

const ADAPT_CLAMP = 4; // bound the slew offset so a transient can't wind it up unboundedly

export class LeadController {
  private adapt = 0; // ±1 slew offset on top of the RTT-derived base lead
  private lastRttMs = -1;
  private lead = INITIAL_LEAD;
  private sinceAdjust = 0; // frames since the last ±1 slew (feedback is delayed ~1 RTT)

  /** D46 Item 6: reset the input-lead steering for a NEW match epoch (fresh tick-0 timeline). */
  reset(): void {
    this.adapt = 0;
    this.lastRttMs = -1;
    this.lead = INITIAL_LEAD;
    this.sinceAdjust = 0;
  }

  /** RTT-derived base lead (§10.2): ceil((rtt/2)/DT) + LEAD_BUFFER + LEAD_JITTER. */
  private baseLead(rttMs: number): number {
    return Math.ceil(rttMs / 2 / DT_MS) + LEAD_BUFFER + LEAD_JITTER;
  }
  /** wait ≈ one RTT between slews so each ±1 change is reflected in the margin before the next (anti-oscillation). */
  private settleFrames(): number {
    return Math.ceil((this.lastRttMs < 0 ? 0 : this.lastRttMs) / DT_MS) + 2;
  }
  private recompute(): void {
    this.lead = this.baseLead(this.lastRttMs < 0 ? 0 : this.lastRttMs) + this.adapt;
  }

  /** Feed a fresh RTT estimate (from ClockSync), once per frame. A change > RTT_READAPT_MS re-adapts. */
  onRtt(rttMs: number): void {
    if (this.lastRttMs >= 0 && Math.abs(rttMs - this.lastRttMs) > RTT_READAPT_MS) {
      this.adapt = 0; // the base lead now covers the new RTT; drop the stale slew offset
      this.sinceAdjust = 0;
    }
    this.lastRttMs = rttMs;
    this.sinceAdjust += 1;
    this.recompute();
  }

  /** Adaptive steering from a snapshot's arrival margin (ackedInputTick − tick): hold it in range. */
  onMargin(margin: number): void {
    if (this.sinceAdjust < this.settleFrames()) return; // let the previous slew propagate (delayed feedback)
    if (margin < MARGIN_MIN && this.adapt < ADAPT_CLAMP) {
      this.adapt += 1; // inputs arriving late ⇒ lead further ahead
      this.sinceAdjust = 0;
    } else if (margin > MARGIN_MAX && this.adapt > -ADAPT_CLAMP) {
      this.adapt -= 1; // wastefully far ahead ⇒ pull the lead back
      this.sinceAdjust = 0;
    }
    this.recompute();
  }

  get currentLead(): number {
    return Math.max(1, this.lead);
  }
  /** the adaptive ±1 slew offset (0 unless onMargin has steered the lead) — for observability/tests. */
  get adaptOffset(): number {
    return this.adapt;
  }
  stampTick(currentServerTick: number): number {
    return currentServerTick + this.currentLead;
  }
}
